import { useEffect, useCallback, useState } from 'react';
import { useFilterStore } from '../store/filterStore';
import { useSound } from './useSound';

interface GridPosition {
  x: number;
  y: number;
}

/**
 * Hook to manage the particle-to-card transition
 * Triggers when user applies first filter in pathology vertical
 * Includes grid position calculation for smooth particle-to-card animation
 */
export const useParticleTransition = () => {
  const {
    vertical,
    activeFilters,
    showParticles,
    isTransitioning,
    setShowParticles,
    setTransitioning,
  } = useFilterStore();

  const { play } = useSound();
  const [cardGridPositions, setCardGridPositions] = useState<GridPosition[]>([]);

  /**
   * Calculate card grid positions based on viewport and expected card count
   */
  const calculateGridPositions = useCallback((cardCount: number): GridPosition[] => {
    const positions: GridPosition[] = [];

    // Responsive column count
    const width = window.innerWidth;
    let columns = 4; // Desktop default (xl)

    if (width < 640) columns = 1; // Mobile (sm)
    else if (width < 768) columns = 2; // Tablet (md)
    else if (width < 1024) columns = 3; // Laptop (lg)

    // Card dimensions (approximate)
    const cardWidth = 240;
    const cardHeight = 280;
    const gap = 16;

    // Calculate grid layout
    // const rows = Math.ceil(cardCount / columns);
    const containerPadding = 32; // p-8 = 32px

    for (let i = 0; i < cardCount; i++) {
      const col = i % columns;
      const row = Math.floor(i / columns);

      const x = containerPadding + col * (cardWidth + gap) + cardWidth / 2;
      const y = containerPadding + row * (cardHeight + gap) + cardHeight / 2;

      positions.push({ x, y });
    }

    return positions;
  }, []);

  /**
   * Start the particle-to-card transition animation
   */
  const startTransition = useCallback(() => {
    // Calculate grid positions for the target cards
    // This would be based on the filtered results count
    const estimatedCardCount = 100; // Initial display count
    const gridPositions = calculateGridPositions(estimatedCardCount);
    setCardGridPositions(gridPositions);

    // Play sound effect
    play('cardFlow');

    // Set transitioning state
    setTransitioning(true);

    // Transition sequence:
    // 1. Phase 1: Preparation (0-0.5s) - highlight particles
    // 2. Phase 2: Movement (0.5-2.0s) - particles move to grid
    // 3. Phase 3: Settle (2.0-2.5s) - cards fade in, particles fade out

    // After transition animation completes (2.5 seconds), hide particles
    setTimeout(() => {
      setShowParticles(false);
      setTransitioning(false);
    }, 2500);
  }, [play, setShowParticles, setTransitioning, calculateGridPositions]);

  /**
   * Reset to show particles (when clearing all filters)
   */
  const resetParticles = useCallback(() => {
    setShowParticles(true);
    setTransitioning(false);
    setCardGridPositions([]);
  }, [setShowParticles, setTransitioning]);

  // Trigger transition when first filter is applied in pathology vertical
  useEffect(() => {
    if (vertical === 'pathology' && activeFilters.length > 0 && showParticles) {
      startTransition();
    }

    // Reset particles when switching back to pathology with no filters
    if (vertical === 'pathology' && activeFilters.length === 0 && !showParticles) {
      resetParticles();
    }
  }, [activeFilters.length, vertical, showParticles, startTransition, resetParticles]);

  // Handle window resize to recalculate grid positions
  useEffect(() => {
    const handleResize = () => {
      if (cardGridPositions.length > 0) {
        const newPositions = calculateGridPositions(cardGridPositions.length);
        setCardGridPositions(newPositions);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [cardGridPositions.length, calculateGridPositions]);

  return {
    shouldShowParticles: vertical === 'pathology' && activeFilters.length === 0,
    isTransitioning,
    startTransition,
    resetParticles,
    cardGridPositions,
  };
};
